// Api Response Types

export enum StreamFormat {
  LIVE_AUDIO_AND_TEXT = "live-audio+text",
  LIVE_AUDIO = "live-audio",
  LIVE_TEXT = "live-text",
  AUDIO_AND_TEXT = "audio+text",
  AUDIO = "audio",
  TEXT = "text",
}

export enum SecurityMode {
  DISABLED = "disabled",
  PASSCODE = "passcode",
  GEOFENCE = "geofence",
  GEOFENCE_AND_PASSCODE = "geofenceAndPasscode",
  GEOFENCE_ELSE_PASSCODE = "geofenceElsePasscode",
}

export interface Venue {
  id: string;
  name: string;
  description: string;
  eventTitle: string;
  rawJson?: string;
}

export interface Geofence {
  latitude: number;
  longitude: number;
  radius: number;
}

export interface DelaySettings {
  mode: "static" | "dynamic";
  desired?: number;
  redundancy?: number;
  min?: number;
  max?: number;
  stepsPerCycle?: number;
  interval?: number;
  lossPercentile?: number;
}

export interface StreamMixInfo {
  id: string;
  name: string;
  image?: string;
  isActive: boolean;
}

export type InterfaceType = "cellular" | "wifi";

export interface StreamInterface {
  type: InterfaceType;
  ssid?: string;
  ssidPassword?: string;
}

export interface Stream {
  id: string;
  format: StreamFormat;
  langCode: string;
  streamUrl: string;
  subscriptionId: string;
  description: string;
  rawJson?: string;
  networkInterface?: StreamInterface;
  parentMix?: StreamMixInfo;
  // LiveAudioStream specific fields
  textStreamUrl?: string;
  textSubscriptionId?: string;
  ioBufferSize?: number;
  delay?: DelaySettings;
}

export interface Mix {
  id: string;
  name: string;
  image?: string;
  active: boolean;
  sessionsEnabled?: boolean;
  sessions: Session[];
}

export interface Session {
  id: string;
  name: string;
  subtitle?: string;
  image?: string;
  startTime?: string;
  endTime?: string;
  recordTime?: string;
  type: "nestedTranslate" | "single";
  passcode?: string;
  parentStreamId: string;
  streams: Stream[];
}

export interface EventMedia {
  id: string;
  venueId: string;
  eventTitle: string;
  allowedOutputTypes: string[];
  securityMode: SecurityMode;
  passcode?: string;
  geofence?: Geofence;
  startTime?: string;
  endTime?: string;
  mixes: Mix[];
  rawJson?: string;
}

// Api Requests

export interface AuthenticationKeys {
  access: string;
  secret: string;
}

export interface APIError {
  message: string;
  status?: number;
}

export interface AuthRequest {
  accessKey: string;
  secretKey: string;
}

export interface AuthResponse {
  token: string;
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  queryParams?: Record<string, string>;
  body?: any;
}

export enum APIRequestQueryKey {
  ACTIVE = "ACTIVE",
  LATITUDE = "LATITUDE",
  LONGITUDE = "LONGITUDE",
}

// Streaming

export type StreamDataHeader = {
  confidence: number;
  has_eos: boolean;
  ingest_time_ms: number;
  is_buffered: boolean;
  is_final: boolean;
  is_punctuation: boolean;
  is_eos: boolean;
  output_duration_micro: number;
  ratio: number;
  since_last_micro: number;
  source_duration_micro: number;
  source_texts: string;
  speaker_label: string;
  synthesizer_publish_time: number;
  text: string;
  topic: string;
  transcriber_origin: string;
  transcriber_publish_time: number;
  transcriber_start_time: number;
  translation_texts: string;
  translator_publish_time: number;
  uuid: string;
  audio_event?: string;
  packed_at_micro?: number;
  timestamp?: number;
  seq_num?: number;
};

export type StreamData = {
  audioStreamData: AudioData | null;
  textStreamData: SpeakerTextData | AudioEventTextData | null;
  serverIngestTimeMs: number | null;
  sinceLastMicro: number | null;
  minStartTime: Date | null;
  isEndOfSentence: boolean;
  receiveTime?: number; // Time when data was received (milliseconds since epoch)
};

export type SpeakerTextData = {
  type: "speech";
  fullTranslationText: string | null;
  translationStreamFragments: TextFragment[];
  sourceStreamFragment: TextFragment | null;
  speakerLabel: string | null;
};

export type AudioEventTextData = {
  type: "audioEvent";
  text: string;
};

export type TextFragment = {
  text: string;
  duration: number;
  isEndOfSentence: boolean;
  speakerLabel: string | null;
};

export type AudioData = {
  timestamp: number;
  channelData: Float32Array[];
  sampleRate: number;
  frameLength: number;
  playbackRate: number;
  uuid: string;
};

export type StreamMetrics = {
  delay: number;
};

// Player

export enum PlayerState {
  Loading = "loading",
  Playing = "playing",
  Stopped = "stopped",
  Failed = "failed",
  Finished = "finished",
}

export type PlayMixResult = {
  mix: Mix;
  session: Session;
  stream: Stream | null;
  selectStream: ((stream: Stream) => void) | null;
};

// Transcription Events
export type SpeechEvent = {
  type: "speech";
  textFragment: TextFragment;
};

export type AudioEvent = {
  type: "audioEvent";
  event: string;
};

export type SpeakerChangeEvent = {
  type: "speakerChange";
  speakerId: string;
};

export type TranscriptionEvent = SpeechEvent | AudioEvent | SpeakerChangeEvent;
